import { Currency, CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { BAULT_ROUTER_ADDRESSES, ISLAND_ROUTER_ADDRESSES } from 'constants/addresses'
import { useMemo } from 'react'
import { parseUnits } from 'viem'

import { useConnection } from './useConnection'
import { useTokenAllowances } from './useTokenAllowance'

interface UseSmartRouterParams {
  mode: 'doubleSided' | 'singleToken' | 'singleNative'
  tokens: (Currency | null | undefined)[]
  needsBaultFeatures: boolean
}

interface RouterContract {
  address: string
}

export function useSmartIslandRouter({
  mode,
  tokens,
  needsBaultFeatures,
}: UseSmartRouterParams): RouterContract | null {
  const { account, chainId } = useConnection()

  const oldIslandRouterAddress = chainId ? ISLAND_ROUTER_ADDRESSES[chainId] : undefined
  const newBaultRouterAddress = chainId ? BAULT_ROUTER_ADDRESSES[chainId] : undefined

  const tokenAllowances = useTokenAllowances(
    tokens.filter((t): t is Token => !!t && !t.isNative),
    account,
    oldIslandRouterAddress
  )

  const hasOldRouterApproval = useMemo(() => {
    if (!oldIslandRouterAddress || !account) return false
    if (mode === 'singleNative') return false

    let allowanceIndex = 0
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (!token || token.isNative) continue

      const allowance = tokenAllowances[allowanceIndex]
      if (!allowance) return false

      const meaningfulThreshold = CurrencyAmount.fromRawAmount(token, parseUnits('1', token.decimals).toString())

      if (allowance.lessThan(meaningfulThreshold)) {
        return false
      }

      allowanceIndex++
    }

    return tokenAllowances.length > 0
  }, [mode, tokens, tokenAllowances, oldIslandRouterAddress, account])

  const selectedRouter = useMemo(() => {
    if (needsBaultFeatures && newBaultRouterAddress) {
      return { address: newBaultRouterAddress }
    }
    if (hasOldRouterApproval && oldIslandRouterAddress) {
      return { address: oldIslandRouterAddress }
    }
    if (newBaultRouterAddress) {
      return { address: newBaultRouterAddress }
    }
    return null
  }, [needsBaultFeatures, hasOldRouterApproval, oldIslandRouterAddress, newBaultRouterAddress])

  return selectedRouter
}
